import './PasswordSetupForm.scss';
import { FormController } from '@form-ts/core';
import { useFormWatch } from '@form-ts/react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { ArrowRightIcon } from 'src/modules/common/icons/ArrowRightIcon';
import { PasswordField } from 'src/modules/form/components/PasswordField';
import { FormError } from 'src/modules/form/types/FormError';
import { PasswordResetFormData } from 'src/modules/password-reset/types/PasswordResetFormData';

type Props = {
  readonly form: FormController<PasswordResetFormData, FormError>;
  readonly onSubmit: () => void;
};

export const PasswordSetupForm = ({ form, onSubmit }: Props): React.ReactElement => {
  const intl = useIntl();
  const renderError = useCallback((e: FormError) => intl.formatMessage({ id: e.message }, e.context), [intl]);

  const handleSubmit = useCallback((event: React.FormEvent) => {
    event.preventDefault();
    onSubmit();
  }, [onSubmit]);

  const isSubmitting = useFormWatch(form, form.submitting.get);

  return (
    <form
      className="bp-password-setup-form"
      noValidate={true}
      autoComplete="off"
      onSubmit={handleSubmit}
    >
      <PasswordField
        className="bp-password-setup-form__field"
        field={form.field.at('password')}
        label={intl.formatMessage({ id: 'passwordSetup/password/label' })}
        renderError={renderError}
        size="md"
        disabled={isSubmitting}
        autoComplete="current-password"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />

      <PasswordField
        className="bp-password-setup-form__field"
        field={form.field.at('confirmation')}
        label={intl.formatMessage({ id: 'passwordSetup/confirmation/label' })}
        renderError={renderError}
        size="md"
        disabled={isSubmitting}
        autoComplete="current-password"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />

      <Button
        kind="primary"
        type="submit"
        className="bp-password-setup-form__submit"
        loading={isSubmitting}
        fullWidth={true}
        size="md"
        icon={(
          <div className="bp-password-setup-form__submit-icon">
            <ArrowRightIcon/>
          </div>
        )}
      >
        <FormattedMessage id="passwordSetup/submit"/>
      </Button>
    </form>
  );
};
